import type { FormOption } from "@/forms/shared/components/searchable-combobox";
import type {
  ContactoErpOption,
  Net4CentroOption,
} from "@/lib/camunda/form-data-service";

export type PrimitiveInput = string | number | boolean | null | undefined;

const NET4_CONTACT_FIELDS = [
  "contacto_erp",
  "contacto_erp_label",
  "numero_contacto",
  "nombre_contacto",
  "email_contacto",
  "telefono_contacto",
  "cargo_contacto",
  "tipo_id_contacto",
  "tipo_id_contacto_label",
  "documento_contacto",
  "firma_contacto",
  "firma_contacto_label",
] as const;

export const NET4_CONTACT_SCOPE_FIELDS = NET4_CONTACT_FIELDS;

export const NET4_CENTER_FIELDS = [
  "net4_centro_codigo",
  "net4_centro_label",
  "numero_centro",
  "nombre_centro",
  "direccion_centro",
  "localidad_centro",
  "codigo_postal_centro",
  "municipio_centro",
  "provincia_centro",
  "provincia_centro_label",
  "zona_geografica",
  "zona_geografica_label",
  "zona_geografica_manual",
  "pais_centro",
  "pais_centro_label",
  "convenio",
  "convenio_label",
  "numero_ss",
  "regimen_ss",
  "regimen_ss_label",
  "cnae",
  "cnae_label",
  ...NET4_CONTACT_FIELDS,
] as const;

export const NET4_CLIENT_FIELDS = [
  "direccion_cliente_net4",
  "direccion_cliente_net4_status",
  "cif",
  "tipo_ident",
  "tipo_ident_label",
  "tipo_ident_catalog_status",
  "iban",
  "tipos_facturacion",
  "tipos_facturacion_label",
  "forma_pago",
  "forma_pago_label",
  "actividad",
  "actividad_label",
  "valor_riesgo_adjudicado",
  "numero_contable",
  "iva",
  "tipo_iva",
  "tipo_iva_label",
  ...NET4_CENTER_FIELDS,
] as const;

export const normalizeEuroAmountForForm = (value: PrimitiveInput): string => {
  const normalized = toTrimmedText(value);
  if (!normalized) {
    return "";
  }

  if (/^\d+\.\d{1,2}$/.test(normalized)) {
    return normalized;
  }

  const commaIndex = normalized.lastIndexOf(",");
  if (commaIndex >= 0) {
    const integerPart = normalized.slice(0, commaIndex).replace(/\./g, "");
    const decimalPart = normalized
      .slice(commaIndex + 1)
      .replace(/[^\d]/g, "")
      .slice(0, 2);
    if (!integerPart && !decimalPart) {
      return "";
    }
    return decimalPart
      ? `${integerPart || "0"}.${decimalPart.padEnd(2, "0")}`
      : integerPart;
  }

  const dotCount = (normalized.match(/\./g) || []).length;
  if (dotCount === 1) {
    const [left = "", right = ""] = normalized.split(".");
    const decimalDigits = right.replace(/[^\d]/g, "");
    if (decimalDigits.length <= 2) {
      return decimalDigits
        ? `${left.replace(/[^\d]/g, "") || "0"}.${decimalDigits.padEnd(2, "0")}`
        : left.replace(/[^\d]/g, "");
    }
  }

  return normalized.replace(/\./g, "");
};

export const toTrimmedText = (value: PrimitiveInput): string => {
  return value == null ? "" : String(value).trim();
};

export const findNet4CenterByCode = (
  centers: Net4CentroOption[],
  centroCodigo: PrimitiveInput,
): Net4CentroOption | null => {
  const normalizedCentro = toTrimmedText(centroCodigo);
  if (!normalizedCentro) {
    return null;
  }
  return (
    centers.find(
      (center) => toTrimmedText(center.codigo || center.value) === normalizedCentro,
    ) ?? null
  );
};

export const resolveDefaultNet4CenterCode = (
  centers: Net4CentroOption[],
  preferredCode: PrimitiveInput,
): string => {
  const preferred = toTrimmedText(preferredCode);
  if (preferred && findNet4CenterByCode(centers, preferred)) {
    return preferred;
  }
  const firstCenter = centers[0];
  return toTrimmedText(firstCenter?.codigo || firstCenter?.value);
};

export const shouldResetNet4Center = (
  scopeChanged: boolean,
  currentCenterCode: string,
  centers: Net4CentroOption[],
): boolean =>
  scopeChanged ||
  !currentCenterCode ||
  !findNet4CenterByCode(centers, currentCenterCode);

export const buildContactScopeKey = (parts: {
  companyId: string;
  dealId: string;
  decision: string;
  clienteCodigo: string;
  centroCodigo: string;
  database: string;
}): string =>
  [
    parts.companyId,
    parts.dealId,
    parts.decision,
    parts.clienteCodigo,
    parts.centroCodigo,
    parts.database,
  ].join(":");

export interface ScopedOptionsState<T> {
  scope: string;
  options: T[];
  loading: boolean;
}

export const resolveScopedOptions = <T>(
  state: ScopedOptionsState<T>,
  expectedScope: string,
): T[] =>
  state.scope === expectedScope ? state.options : [];

export const resolveScopedOptionsLoading = <T>(
  state: ScopedOptionsState<T>,
  expectedScope: string,
  canLoad: boolean,
): boolean =>
  canLoad && (state.scope !== expectedScope || state.loading);

const mapFirmaContactoFromNet4 = (value: PrimitiveInput) => {
  const normalized = toTrimmedText(value).toUpperCase();
  if (normalized === "S") {
    return { value: "S", label: "Sí" };
  }
  return { value: "N", label: "No" };
};

export interface Net4ContactFields {
  contacto_erp: string;
  contacto_erp_label: string;
  numero_contacto: string;
  nombre_contacto: string;
  email_contacto: string;
  telefono_contacto: string;
  cargo_contacto: string;
  tipo_id_contacto: string;
  tipo_id_contacto_label: string;
  documento_contacto: string;
  firma_contacto: string;
  firma_contacto_label: string;
}

export const resolveNet4ContactFields = (
  contact: ContactoErpOption | null,
  includeFirmaContacto: boolean,
): Net4ContactFields => {
  const firma = includeFirmaContacto
    ? mapFirmaContactoFromNet4(contact?.firmaContacto)
    : { value: "", label: "" };
  return {
    contacto_erp: toTrimmedText(contact?.value),
    contacto_erp_label: toTrimmedText(contact?.label),
    numero_contacto: toTrimmedText(contact?.ordenContacto),
    nombre_contacto: toTrimmedText(contact?.label),
    email_contacto: toTrimmedText(contact?.email),
    telefono_contacto: toTrimmedText(contact?.telefono),
    cargo_contacto: toTrimmedText(contact?.cargo),
    tipo_id_contacto: toTrimmedText(contact?.tipoId),
    tipo_id_contacto_label: toTrimmedText(contact?.tipoIdLabel),
    documento_contacto: toTrimmedText(contact?.documento),
    firma_contacto: firma.value,
    firma_contacto_label: firma.label,
  };
};

export const hasNet4ScopeChanged = (
  previousScope: string | null,
  currentScope: string,
): boolean =>
  previousScope !== null &&
  previousScope !== currentScope;

export const toCenterFormOptions = (centers: Net4CentroOption[]): FormOption[] =>
  centers.map((center) => {
    const code = toTrimmedText(center.codigo || center.value);
    const name = toTrimmedText(center.nombre || center.label);
    return {
      value: code,
      label: name || `Centro ${code}`,
      displayLabel: center.displayLabel,
    };
  });

export const findOptionLabel = (options: FormOption[], value: PrimitiveInput): string => {
  const normalizedValue = toTrimmedText(value);
  if (!normalizedValue) {
    return "";
  }
  return options.find((option) => option.value === normalizedValue)?.label || "";
};

const normalizeProductFamilyKey = (value: PrimitiveInput): string => {
  const text = value == null ? "" : String(value);
  return text
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, " ");
};

export const isEttProductFamily = (value: PrimitiveInput): boolean =>
  normalizeProductFamilyKey(toTrimmedText(value)) === "ett";

export const usesNet4ClientSource = (decision: PrimitiveInput): boolean => {
  const normalizedDecision = toTrimmedText(decision);
  return (
    normalizedDecision === "exists" ||
    normalizedDecision === "center_exists" ||
    normalizedDecision === "modify_client"
  );
};

export const resolveHubSpotImporteRiesgoProperty = (
  productFamily: PrimitiveInput,
): "importe_riesgo_ett" | "importe_riesgo_servicios" =>
  isEttProductFamily(productFamily) ? "importe_riesgo_ett" : "importe_riesgo_servicios";
